import React, { useState, useEffect } from 'react';
import { reportApi } from '../services/api';
import { Card, Spinner, Badge } from '../components/UI';
import { Briefcase, TrendUp, TrendDown, CurrencyDollar, ChartBar, ArrowUp, ArrowDown } from '@phosphor-icons/react';

import { fmtCurrency, fmtCurrencyShort } from '../utils/currencyFormat';

export default function ExecutiveDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reportApi.executiveDashboard().then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const fmt = (n) => fmtCurrencyShort(Math.abs(n || 0));

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;
  if (!data) return <Card className="p-8 text-center text-[#666]">Failed to load dashboard</Card>;

  return (
    <div className="space-y-5" data-testid="executive-dashboard-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A]">Executive Dashboard</h1>
        <p className="text-sm text-[#666]">Organization-wide project & financial overview</p>
      </div>

      {/* KPI Row 1 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="exec-kpis">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1"><Briefcase className="w-4 h-4 text-[#002FA7]" /><span className="text-[10px] text-[#999] uppercase">Total Projects</span></div>
          <p className="text-2xl font-bold text-[#0A0A0A]">{data.total_projects}</p>
          <p className="text-xs text-[#666]">{data.active_projects} active · {data.completed_projects} completed</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1"><CurrencyDollar className="w-4 h-4 text-[#002FA7]" /><span className="text-[10px] text-[#999] uppercase">Contract Value</span></div>
          <p className="text-2xl font-bold text-[#002FA7]">{fmt(data.total_contract_value)}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1"><TrendDown className="w-4 h-4 text-[#FF3333]" /><span className="text-[10px] text-[#999] uppercase">Total Cost</span></div>
          <p className="text-2xl font-bold text-[#FF3333]">{fmt(data.total_cost)}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1"><TrendUp className="w-4 h-4 text-[#00C853]" /><span className="text-[10px] text-[#999] uppercase">Revenue</span></div>
          <p className="text-2xl font-bold text-[#00C853]">{fmt(data.total_revenue)}</p>
        </Card>
      </div>

      {/* KPI Row 2 */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 text-center">
          <p className="text-[10px] text-[#999] uppercase">Profit</p>
          <p className={`text-2xl font-bold ${data.profit >= 0 ? 'text-[#00C853]' : 'text-[#FF3333]'}`}>
            {data.profit >= 0 ? '+' : '-'}{fmt(data.profit)}
          </p>
          {data.margin_pct !== 0 && <p className="text-xs text-[#666]">{data.margin_pct}% margin</p>}
        </Card>
        <Card className="p-4 text-center">
          <p className="text-[10px] text-[#999] uppercase">Payments Received</p>
          <p className="text-2xl font-bold text-[#00C853]">{fmt(data.total_payments)}</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-[10px] text-[#999] uppercase">Outstanding</p>
          <p className="text-2xl font-bold text-[#FFAB00]">{fmt(data.outstanding)}</p>
        </Card>
      </div>

      {/* Project Breakdown */}
      {data.projects.length > 0 && (
        <Card className="p-4" data-testid="project-breakdown">
          <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3">Project Cost vs Contract</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                  <th className="pb-2">Project</th><th className="pb-2">Status</th>
                  <th className="pb-2 text-right">Contract</th><th className="pb-2 text-right">Actual Cost</th>
                  <th className="pb-2 text-right">Variance</th>
                </tr>
              </thead>
              <tbody>
                {data.projects.map((p, i) => {
                  const variance = (p.contract_value || 0) - p.actual_cost;
                  return (
                    <tr key={i} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]">
                      <td className="py-2 font-medium">{p.name}</td>
                      <td className="py-2"><Badge variant={p.status === 'ACTIVE' ? 'success' : p.status === 'COMPLETED' ? 'primary' : 'default'}>{p.status}</Badge></td>
                      <td className="py-2 text-right text-[#002FA7] font-semibold">{fmt(p.contract_value)}</td>
                      <td className="py-2 text-right">{fmt(p.actual_cost)}</td>
                      <td className={`py-2 text-right font-bold ${variance >= 0 ? 'text-[#00C853]' : 'text-[#FF3333]'}`}>
                        {variance >= 0 ? '+' : '-'}{fmt(variance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
